import { AdvancedMarker } from '@vis.gl/react-google-maps'
import { useMemo } from 'react'
import { type GoogleDeckDomEvent, useGoogleMapViewport } from '../google'
import type { ListingPointFeature } from './listingPointFeatures'

export const GOOGLE_IMAGE_MARKER_MIN_ZOOM = 16
export const GOOGLE_IMAGE_MARKER_SIZE_PX = 48

type GoogleImageMarkerItem = {
  id: string
  imageUrl: string
  name: string
  position: google.maps.LatLngLiteral
}

function getImageMarkerItems(
  features: ListingPointFeature[]
): GoogleImageMarkerItem[] {
  return features
    .filter((feature) => feature.properties.thumbUrl)
    .map((feature) => ({
      id: feature.properties._id,
      imageUrl: feature.properties.thumbUrl!,
      name: feature.properties.name,
      position: {
        lat: feature.properties.lat,
        lng: feature.properties.lng,
      },
    }))
}

export type GoogleImageMarkersProps = {
  features: ListingPointFeature[]
  onListingClick?: (
    feature: ListingPointFeature,
    domEvent: GoogleDeckDomEvent | null
  ) => void
}

export function GoogleImageMarkers({
  features,
  onListingClick,
}: GoogleImageMarkersProps) {
  const viewport = useGoogleMapViewport()
  const imageMarkers = useMemo(() => {
    if (!viewport || viewport.zoom <= GOOGLE_IMAGE_MARKER_MIN_ZOOM) return []

    return getImageMarkerItems(features)
  }, [features, viewport])

  return (
    <>
      {imageMarkers.map((marker) => (
        <AdvancedMarker
          key={marker.id}
          position={marker.position}
          clickable={Boolean(onListingClick)}
          anchorLeft="-50%"
          anchorTop="calc(-100% - 3px)"
          onClick={(event) => {
            const feature = features.find(
              (listingFeature) => listingFeature.properties._id === marker.id
            )

            event.domEvent?.stopPropagation()

            if (feature) {
              onListingClick?.(feature, event.domEvent ?? null)
            }
          }}
          zIndex={50}>
          <div className="pointer-events-none relative isolate select-none drop-shadow-md">
            <div className="absolute left-1/2 top-full -mt-1.5 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-background" />
            <img
              alt={marker.name}
              className="relative z-10 rounded-full border-2 border-background bg-background object-cover"
              draggable={false}
              height={GOOGLE_IMAGE_MARKER_SIZE_PX}
              src={marker.imageUrl}
              style={{
                height: `${GOOGLE_IMAGE_MARKER_SIZE_PX}px`,
                width: `${GOOGLE_IMAGE_MARKER_SIZE_PX}px`,
              }}
              width={GOOGLE_IMAGE_MARKER_SIZE_PX}
            />
          </div>
        </AdvancedMarker>
      ))}
    </>
  )
}
